import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConfirmDialogVariant = "default" | "destructive";

export interface ConfirmDialogOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
}

export interface ConfirmDialogProps extends ConfirmDialogOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel?: () => void;
}

/**
 * Titles get the brand's accent period, the same way every dialog header in the
 * kit does — but only when the sentence does not already end itself. Most
 * confirm titles are questions ("Delete "Invoice 1042"?"), and "…?." is not a
 * sentence. Non-string titles (a node with a bolded filename inside) are left
 * exactly as passed, so a call site keeps full control when it needs it.
 */
function withAccentPeriod(title: React.ReactNode): React.ReactNode {
  if (typeof title !== "string") return title;
  if (/[?.!:…]$/.test(title.trimEnd())) return title;
  return (
    <>
      {title}
      <span className="text-[#f70000]">.</span>
    </>
  );
}

/**
 * Declarative shadcn-based replacement for window.confirm().
 *
 * Use directly when you already manage your own open state, or via
 * `useConfirm()` for an imperative `await confirm(...)` API.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel?.();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader className="space-y-1">
          <AlertDialogTitle className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
            {withAccentPeriod(title)}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="text-xs leading-relaxed text-muted-foreground">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* rounded-full: LightMaster buttons are pills, everywhere. */}
          <AlertDialogCancel className="rounded-full">{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              "rounded-full",
              variant === "destructive" &&
                cn(buttonVariants({ variant: "destructive" }), "rounded-full"),
            )}
            onClick={() => {
              onConfirm();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Imperative hook API: `const confirm = useConfirm(); if (await confirm(...))`
// ---------------------------------------------------------------------------

type ConfirmFn = (options: ConfirmDialogOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmDialogOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  // Track whether the user took an explicit action so onOpenChange's "close"
  // branch only resolves with `false` when escaping/backdrop.
  const resolvedRef = React.useRef(false);

  const confirm = React.useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // A second confirm while one is open replaces the dialog. The earlier
      // caller must NOT be left awaiting forever (its revert/cleanup path
      // would never run) — it resolves false, exactly as if dismissed.
      setPending((prev) => {
        if (prev && !resolvedRef.current) prev.resolve(false);
        resolvedRef.current = false;
        return { ...options, resolve };
      });
    });
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (pending && !resolvedRef.current) {
      pending.resolve(false);
      resolvedRef.current = true;
    }
    setPending(null);
  };

  const handleConfirm = () => {
    if (!pending) return;
    resolvedRef.current = true;
    pending.resolve(true);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={handleOpenChange}
        title={pending?.title ?? ""}
        description={pending?.description}
        confirmLabel={pending?.confirmLabel}
        cancelLabel={pending?.cancelLabel}
        variant={pending?.variant}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a ConfirmDialogProvider");
  }
  return ctx;
}
